import React, { useMemo, useRef, useCallback, forwardRef, Fragment } from "react";
import { useSelector } from "react-redux";
import { ScrollShadow, ScrollElement, SpinnerLoader } from "@activecollab/components";
import classnames from "classnames";
import moment from "moment";
import PropTypes from "prop-types";
import { BodyItem } from "./BodyElements/BodyItem.jsx";
import { DateSeparator } from "./BodyElements/DateSeparator.jsx";
import { NewMessageSeparator } from "./BodyElements/NewMessageSeparator.jsx";
import { UserNameElement } from "./BodyElements/UserNameElement.jsx";
import * as SystemMessages from "./Message/SystemMessage/index.jsx";
import { messageTypes } from "./Message/SystemMessage/types/index.jsx";
import {
  getPersonalizedDateFormat,
  getPersonalizedTimeFormat,
} from "../../../../store/selectors/selector.configOptions.jsx";
import { makeGetConversationMessagesSelector } from "../../../../store/selectors/selector.conversationMessages.jsx";
import WhoIsWriting from "../../../WhoIsWriting.jsx";

const getUserMessage = (next = false, messageIndex, messages = []) => {
  let message;

  let i = 1;
  message = next ? messages[messageIndex + i] : messages[messageIndex - i];
  const messagesLength = messages.length;

  if (!message || messageTypes[message?.class] === "UserMessage") {
    return message;
  }

  return next
    ? messages
        .slice(messageIndex + 1, messagesLength - 1)
        .find((message) => messageTypes[message?.class] === "UserMessage")
    : messages
        .slice(0, messageIndex - 1)
        .reverse()
        .find((message) => messageTypes[message?.class] === "UserMessage");
};

export const Body = forwardRef(
  (
    { conversationId, loggedUserId, onFetchMore, loading, onDelete, onClose, onEdit, onResend, type, newMessagesSince },
    ref
  ) => {
    const fakeElRef = useRef();
    const getConversationMessages = useMemo(
      () => makeGetConversationMessagesSelector(conversationId),
      [conversationId]
    );
    const messages = useSelector(getConversationMessages);
    const formatDate = useSelector(getPersonalizedDateFormat);
    const formatTime = useSelector(getPersonalizedTimeFormat);

    const sortedMessages = useMemo(() => {
      return messages.map((message, index) => {
        const show = {
          avatar: false,
          date_separator: false,
          username: false,
          new_message_separator: false,
        };

        const { class: classType } = message;
        const messageType = messageTypes[classType];

        const prevMessage = messages[index - 1];
        const nextMessage = messages[index + 1];

        // Checking whether the PREVIOUS message was created on the
        // same day, if not then the current one is marked by
        // rendering the given separator and
        // the name of the message creator.
        const prevDate = moment.unix(prevMessage?.created_on);
        const nextDate = moment.unix(message?.created_on);
        if (!prevDate.isSame(nextDate, "day") || nextDate.diff(prevDate, "minutes") > 30) {
          show.date_separator = true;
        }

        if (messageType !== "UserMessage") {
          return { ...message, show };
        }

        let prevMessageType = "";
        let nextMessageType = "";

        if (prevMessage) {
          const { class: prevMessageClassType } = prevMessage;
          prevMessageType = messageTypes[prevMessageClassType];
        }
        if (nextMessage) {
          const { class: nextMessageClassType } = nextMessage;
          nextMessageType = messageTypes[nextMessageClassType];
        }

        // Check if NEXT message was created by same user
        // if not, mark it to render avatar.
        if (nextMessage?.created_by_id !== message.created_by_id || nextMessageType !== "UserMessage") {
          show.avatar = true;
        }

        // Check if the PREVIOUS message is from the same user, if not
        // then the current one is marked by rendering the name of
        // the creator of the message.
        if (
          (prevMessage?.created_by_id !== message.created_by_id || prevMessageType !== "UserMessage") &&
          type !== "OneToOneConversation" &&
          loggedUserId !== message.created_by_id
        ) {
          show.username = true;
        }

        const prevUserMessage =
          prevMessageType !== "UserMessage" ? getUserMessage(false, index, messages) : prevMessage;
        const createdOn = prevUserMessage?.created_on || 0;

        // Check that the PREVIOUS message is older than current and current is younger
        // than the time the user last accessed the conversation.
        if (
          createdOn <= newMessagesSince &&
          newMessagesSince < message.created_on &&
          loggedUserId !== message.created_by_id
        ) {
          show.new_message_separator = true;
        }

        return { ...message, show };
      });
    }, [messages, newMessagesSince, loggedUserId, type]);

    const handleScroll = useCallback(
      (onScroll) => (event) => {
        const top = -1 * (event?.target?.scrollTop || 0);
        const height = event?.target?.scrollHeight || 0;
        const offsetHeight = event?.target?.offsetHeight || 0;

        if (typeof onFetchMore === "function" && height - offsetHeight - 100 <= top) {
          onFetchMore();
        }
        onScroll(event);
      },
      [onFetchMore]
    );

    return (
      <div className="tw-flex tw-overflow-y-auto tw-grow tw-flex-1 conversation-body">
        <ScrollShadow
          className="tw-grow tw-min-h-0 tw-flex"
          disableHorizontal
          invertVertical
        >
          {({ onScroll }) => (
            <ScrollElement
              ref={ref}
              className="tw-h-full tw-w-full tw-flex tw-flex-col-reverse"
              onScroll={handleScroll(onScroll)}
              invert
            >
              <div className="tw-shrink tw-flex">
                <div className="tw-w-full tw-flex tw-flex-col">
                  <div className="tw-relative">
                    {loading ? (
                      <div className="tw-flex tw-items-center tw-justify-center tw-py-3">
                        <SpinnerLoader />
                      </div>
                    ) : null}
                  </div>
                  {sortedMessages.map(
                    ({
                      id,
                      body_formatted,
                      created_on,
                      loading = false,
                      changed_on,
                      error = false,
                      body,
                      created_by_id,
                      show,
                      class: classType,
                      attachments,
                      created_by_name,
                      created_by_email,
                    }) => {
                      const loggedUserGroup = loggedUserId === created_by_id;

                      const messageType = messageTypes[classType];
                      const SystemMessage = SystemMessages[messageType];

                      return (
                        <Fragment key={`conversation-message-${id}`}>
                          {show?.date_separator ? <DateSeparator timestamp={created_on} /> : null}
                          {show?.new_message_separator ? <NewMessageSeparator className="tw-mt-4" /> : null}
                          {show?.username ? <UserNameElement userId={created_by_id} /> : null}
                          <BodyItem
                            className={classnames({
                              "tw-justify-end": loggedUserGroup,
                              "tw-justify-start": !loggedUserGroup,
                              "tw-justify-center": messageType !== "UserMessage",
                            })}
                          >
                            {SystemMessage ? (
                              <SystemMessage
                                id={id}
                                onDelete={onDelete}
                                onClose={onClose}
                                onEdit={onEdit}
                                left={loggedUserGroup}
                                pending={loading}
                                error={error}
                                createdOn={created_on}
                                formatDate={formatDate}
                                formatTime={formatTime}
                                changedOn={changed_on}
                                onResend={onResend}
                                body={body}
                                showAvatar={show?.avatar}
                                createdById={created_by_id}
                                type={type}
                                messageText={body_formatted}
                                messageType={messageType}
                                attachments={attachments}
                                conversationId={conversationId}
                                createdByName={created_by_name}
                                createdByEmail={created_by_email}
                              />
                            ) : null}
                          </BodyItem>
                        </Fragment>
                      );
                    }
                  )}
                  <WhoIsWriting
                    context="message"
                    className="chat-who-is-writing tw-mb-2"
                  />
                  <div
                    style={{ float: "left", clear: "both" }}
                    ref={fakeElRef}
                  />
                </div>
              </div>
            </ScrollElement>
          )}
        </ScrollShadow>
      </div>
    );
  }
);

Body.propTypes = {
  conversationId: PropTypes.number.isRequired,
  loggedUserId: PropTypes.number.isRequired,
  onFetchMore: PropTypes.func,
  onClose: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onResend: PropTypes.func,
  type: PropTypes.string,
  loading: PropTypes.bool,
  newMessagesSince: PropTypes.number,
};

Body.defaultProps = {
  loading: false,
};

Body.displayName = "Body";
